import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';
import { IntegrationCard } from './IntegrationCard';

interface ZohoContact {
  id: string;
  Full_Name?: string;
  Email?: string;
  Account_Name?: {
    name?: string;
  };
}

export const ZohoOAuthIntegration = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [contacts, setContacts] = useState<ZohoContact[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);

  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    setChecking(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('crm_integrations')
        .select('*')
        .eq('user_id', user.id)
        .eq('crm_type', 'zoho')
        .eq('status', 'active')
        .maybeSingle();

      setIsConnected(!!data);
      setLastSyncedAt(data?.last_sync_at ?? null);
    } catch (error) {
      console.error('Error checking Zoho connection:', error);
    } finally {
      setChecking(false);
    }
  };

  const handleOAuthConnect = async () => {
    setIsConnecting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Call the OAuth initialization edge function
      const { data, error } = await supabase.functions.invoke('crm-oauth-initialize', {
        body: { crmType: 'zoho' },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to initialize OAuth');
      }

      if (data?.authUrl) {
        // Redirect to OAuth authorization URL
        window.location.href = data.authUrl;
      } else {
        throw new Error('No authorization URL received');
      }
    } catch (error: any) {
      console.error('Zoho OAuth error:', error);
      toast.error(error.message || 'Failed to start OAuth flow');
      setIsConnecting(false);
    }
  };

  const loadContacts = async () => {
    setIsLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("Please sign in first");
        return;
      }

      const { data, error } = await supabase.functions.invoke("crm-data", {
        body: { crm_type: "zoho", action: "list_contacts" },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw new Error(error.message);
      if (!data?.success) {
        toast.error(data?.error || "Failed to load Zoho contacts");
        if ((data?.error || "").toLowerCase().includes("not connected")) {
          setIsConnected(false);
        }
        return;
      }

      setContacts(data.data || []);
      setIsConnected(true);
      toast.success(`Loaded ${data.count} real contacts from Zoho`);
    } catch (error: any) {
      console.error("Error loading contacts:", error);
      toast.error(error.message || "Failed to load contacts from Zoho");
    } finally {
      setIsLoading(false);
    }
  };

  const runAIWorkflow = async () => {
    try {
      toast.info('Starting AI workflow with Zoho data...');
      // Trigger AI workflow that will process Zoho data
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { error } = await supabase.functions.invoke('enhanced-data-refresh', {
        body: { 
          source: 'zoho',
          includeAIWorkflow: true
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      toast.success('AI workflow started successfully!');
    } catch (error: any) {
      console.error('Error running AI workflow:', error);
      toast.error('Failed to run AI workflow');
    }
  };

  const disconnect = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('crm_integrations')
        .delete()
        .eq('user_id', user.id)
        .eq('crm_type', 'zoho');

      setIsConnected(false);
      setContacts([]);
      toast.success('Disconnected from Zoho');
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Failed to disconnect from Zoho');
    }
  };

  return (
    <IntegrationCard
      icon={
        <img
          src="https://www.zohowebstatic.com/sites/zweb/images/logo/zoho-logo.svg"
          alt="Zoho"
          className="w-6 h-6"
        />
      }
      name="Zoho CRM"
      description="Sign in with your own Zoho account to read your contacts, deals and accounts."
      isConnected={isConnected}
      checking={checking}
      isConnecting={isConnecting}
      lastSyncedAt={lastSyncedAt}
      onConnect={handleOAuthConnect}
      onDisconnect={disconnect}
      actions={
        <>
          <Button onClick={loadContacts} disabled={isLoading} variant="outline">
            {isLoading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading…</>
            ) : (
              'Load Contacts'
            )}
          </Button>
          <Button onClick={runAIWorkflow} variant="outline">Run AI Workflow</Button>
        </>
      }
    >
      {contacts.length > 0 && (
        <div>
          <h4 className="font-medium mb-2">Contacts ({contacts.length})</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {contacts.map((contact) => (
              <div key={contact.id} className="p-3 border rounded-lg">
                <div className="font-medium">{contact.Full_Name || contact.Email || contact.id}</div>
                <div className="text-sm text-muted-foreground">
                  {[contact.Email, contact.Account_Name?.name].filter(Boolean).join(' · ')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </IntegrationCard>
  );
};
